import { Temporal } from "temporal-polyfill";
import { AffiliationStatus } from "@api/shared-runtime/enums";
import type { RouterOutputs } from "@/trpc";

export type Candidate =
  RouterOutputs["jobPost"]["getInterestedCandidates"][number];

export type HiringStage = { id: string; name: string };

export enum CandidateViewKind {
  All = "all",
  Hired = "hired",
  Rejected = "rejected",
  Withdrawn = "withdrawn",
}

const STAGE_VIEW_PREFIX = "stage:";

export type CandidateViewKey =
  | CandidateViewKind
  | `${typeof STAGE_VIEW_PREFIX}${string}`;

export enum CandidateSort {
  NewestApplied = "newest-applied",
  OldestApplied = "oldest-applied",
  NameAscending = "name-ascending",
}

export const CANDIDATE_SORT_LABELS: Record<CandidateSort, string> = {
  [CandidateSort.NewestApplied]: "Newest applied",
  [CandidateSort.OldestApplied]: "Oldest applied",
  [CandidateSort.NameAscending]: "Name A-Z",
};

export const NEW_CANDIDATE_WINDOW_DAYS = 7;
export const CANDIDATES_PER_PAGE = 12;
const HIDDEN_PROVIDER_ID_LENGTH = 4;

export enum MessagedFilter {
  Any = "any",
  Messaged = "messaged",
  NotMessaged = "not-messaged",
  AwaitingCandidateReply = "awaiting-candidate-reply",
  AwaitingOrganizationReply = "awaiting-organization-reply",
}

export const MESSAGED_FILTER_LABELS: Record<MessagedFilter, string> = {
  [MessagedFilter.Any]: "Any",
  [MessagedFilter.Messaged]: "Messaged",
  [MessagedFilter.NotMessaged]: "Not messaged",
  [MessagedFilter.AwaitingCandidateReply]: "Awaiting candidate reply",
  [MessagedFilter.AwaitingOrganizationReply]: "Awaiting our reply",
};

export enum RevealedFilter {
  Any = "any",
  Revealed = "revealed",
  NotRevealed = "not-revealed",
}

export const REVEALED_FILTER_LABELS: Record<RevealedFilter, string> = {
  [RevealedFilter.Any]: "Any",
  [RevealedFilter.Revealed]: "Revealed",
  [RevealedFilter.NotRevealed]: "Not revealed",
};

export type CandidateFilters = {
  professionalTypeId: string | null;
  specialtyId: string | null;
  revealed: RevealedFilter;
  messaged: MessagedFilter;
};

export const EMPTY_CANDIDATE_FILTERS: CandidateFilters = {
  professionalTypeId: null,
  specialtyId: null,
  revealed: RevealedFilter.Any,
  messaged: MessagedFilter.Any,
};

export function stageViewKey(stageId: string): CandidateViewKey {
  return `${STAGE_VIEW_PREFIX}${stageId}`;
}

export function getStageIdFromViewKey(view: CandidateViewKey): string | null {
  if (view.startsWith(STAGE_VIEW_PREFIX)) {
    return view.slice(STAGE_VIEW_PREFIX.length);
  }
  return null;
}

export function isCandidateRejected(candidate: Candidate): boolean {
  return !!candidate.rejectedAt;
}

// A rejection supersedes a withdrawal, so rejected candidates never show up
// in the withdrawn view even if they withdrew first.
export function isCandidateWithdrawn(candidate: Candidate): boolean {
  return !!candidate.withdrawnAt && !candidate.rejectedAt;
}

export function isCandidateHired(candidate: Candidate): boolean {
  return candidate.affiliationStatus === AffiliationStatus.Hired;
}

// "All" is the working set: everyone who is neither rejected nor withdrawn.
function isCandidateInAllView(candidate: Candidate): boolean {
  return !isCandidateRejected(candidate) && !isCandidateWithdrawn(candidate);
}

export function isCandidateHidden(candidate: Candidate): boolean {
  return !candidate.isRevealed;
}

export function getCandidateDisplayName(candidate: Candidate): string {
  if (isCandidateHidden(candidate)) {
    return `Provider #${candidate.userId.slice(-HIDDEN_PROVIDER_ID_LENGTH)}`;
  }
  return `${candidate.firstName ?? ""} ${candidate.lastName ?? ""}`.trim();
}

export function isCandidateInView({
  candidate,
  view,
}: {
  candidate: Candidate;
  view: CandidateViewKey;
}): boolean {
  switch (view) {
    case CandidateViewKind.All:
      return isCandidateInAllView(candidate);
    case CandidateViewKind.Hired:
      return isCandidateInAllView(candidate) && isCandidateHired(candidate);
    case CandidateViewKind.Rejected:
      return isCandidateRejected(candidate);
    case CandidateViewKind.Withdrawn:
      return isCandidateWithdrawn(candidate);
  }
  const stageId = getStageIdFromViewKey(view);
  return (
    stageId !== null &&
    isCandidateInAllView(candidate) &&
    !isCandidateHired(candidate) &&
    candidate.hiringProcessCurrentStageId === stageId
  );
}

export function filterCandidatesByView({
  candidates,
  view,
}: {
  candidates: Candidate[];
  view: CandidateViewKey;
}): Candidate[] {
  return candidates.filter((candidate) =>
    isCandidateInView({ candidate, view }),
  );
}

export type CandidateViewCounts = {
  all: number;
  hired: number;
  rejected: number;
  withdrawn: number;
  byStageId: Record<string, number>;
};

export function countCandidatesByView({
  candidates,
  stages,
}: {
  candidates: Candidate[];
  stages: HiringStage[];
}): CandidateViewCounts {
  const byStageId: Record<string, number> = {};
  for (const stage of stages) {
    byStageId[stage.id] = filterCandidatesByView({
      candidates,
      view: stageViewKey(stage.id),
    }).length;
  }
  return {
    all: filterCandidatesByView({ candidates, view: CandidateViewKind.All })
      .length,
    hired: filterCandidatesByView({ candidates, view: CandidateViewKind.Hired })
      .length,
    rejected: filterCandidatesByView({
      candidates,
      view: CandidateViewKind.Rejected,
    }).length,
    withdrawn: filterCandidatesByView({
      candidates,
      view: CandidateViewKind.Withdrawn,
    }).length,
    byStageId,
  };
}

// Counts candidates in the All view who showed interest within the last
// NEW_CANDIDATE_WINDOW_DAYS days.
export function countNewCandidates({
  candidates,
  now,
}: {
  candidates: Candidate[];
  now: Temporal.Instant;
}): number {
  const cutoff = now.subtract({ hours: NEW_CANDIDATE_WINDOW_DAYS * 24 });
  return candidates.filter((candidate) => {
    if (!isCandidateInAllView(candidate) || !candidate.interestedAt) {
      return false;
    }
    const interestedAt = Temporal.Instant.fromEpochMilliseconds(
      new Date(candidate.interestedAt).getTime(),
    );
    return Temporal.Instant.compare(interestedAt, cutoff) >= 0;
  }).length;
}

// "In process" means actively moving through the pipeline: in the All view,
// not yet hired, and sitting in any stage after the job's first one.
export function countInProcessCandidates({
  candidates,
  stages,
}: {
  candidates: Candidate[];
  stages: HiringStage[];
}): number {
  const laterStageIds = new Set(stages.slice(1).map((stage) => stage.id));
  return candidates.filter(
    (candidate) =>
      isCandidateInAllView(candidate) &&
      !isCandidateHired(candidate) &&
      candidate.hiringProcessCurrentStageId !== null &&
      laterStageIds.has(candidate.hiringProcessCurrentStageId),
  ).length;
}

export type FilterOption = { id: string; name: string };

function addOption(options: Map<string, string>, option: FilterOption | null) {
  if (option) {
    options.set(option.id, option.name);
  }
}

function sortOptionsByName(options: Map<string, string>): FilterOption[] {
  return Array.from(options, ([id, name]) => ({ id, name })).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

export function getCandidateFilterOptions(candidates: Candidate[]): {
  professionalTypes: FilterOption[];
  specialties: FilterOption[];
} {
  const professionalTypes = new Map<string, string>();
  const specialties = new Map<string, string>();
  for (const candidate of candidates) {
    addOption(professionalTypes, candidate.healthcareProfessionalType ?? null);
    for (const specialty of candidate.specialties ?? []) {
      addOption(specialties, specialty);
    }
  }
  return {
    professionalTypes: sortOptionsByName(professionalTypes),
    specialties: sortOptionsByName(specialties),
  };
}

export function countActiveFilters(filters: CandidateFilters): number {
  return [
    filters.professionalTypeId !== null,
    filters.specialtyId !== null,
    filters.revealed !== RevealedFilter.Any,
    filters.messaged !== MessagedFilter.Any,
  ].filter(Boolean).length;
}

// "Messaged" means the team wrote since the candidate applied
// (lastOrgMessageAt), so AI follow-ups and older threads don't count as
// outreach. lastMessage is the newest message in either direction, so
// "awaiting candidate reply" is "we reached out and the newest message came
// from us". "Awaiting our reply" only needs the newest message to be the
// candidate's: it includes candidates who wrote first and those answering an
// AI follow-up, since either way the team owes them a response.
function matchesMessagedFilter({
  candidate,
  messaged,
}: {
  candidate: Candidate;
  messaged: MessagedFilter;
}): boolean {
  const { lastMessage, lastOrgMessageAt } = candidate;
  switch (messaged) {
    case MessagedFilter.Any:
      return true;
    case MessagedFilter.Messaged:
      return lastOrgMessageAt !== null;
    case MessagedFilter.NotMessaged:
      return lastOrgMessageAt === null;
    case MessagedFilter.AwaitingCandidateReply:
      return (
        lastOrgMessageAt !== null && lastMessage?.fromOrganization === true
      );
    case MessagedFilter.AwaitingOrganizationReply:
      return lastMessage?.fromOrganization === false;
  }
}

function matchesRevealedFilter({
  candidate,
  revealed,
}: {
  candidate: Candidate;
  revealed: RevealedFilter;
}): boolean {
  switch (revealed) {
    case RevealedFilter.Any:
      return true;
    case RevealedFilter.Revealed:
      return !isCandidateHidden(candidate);
    case RevealedFilter.NotRevealed:
      return isCandidateHidden(candidate);
  }
}

export function filterCandidates({
  candidates,
  filters,
}: {
  candidates: Candidate[];
  filters: CandidateFilters;
}): Candidate[] {
  return candidates.filter((candidate) => {
    if (
      filters.professionalTypeId !== null &&
      candidate.healthcareProfessionalType?.id !== filters.professionalTypeId
    ) {
      return false;
    }
    if (filters.specialtyId !== null) {
      const hasSpecialty = (candidate.specialties ?? []).some(
        (specialty) => specialty.id === filters.specialtyId,
      );
      if (!hasSpecialty) {
        return false;
      }
    }
    if (!matchesRevealedFilter({ candidate, revealed: filters.revealed })) {
      return false;
    }
    return matchesMessagedFilter({
      candidate,
      messaged: filters.messaged,
    });
  });
}

export const IMLC_LICENSE_LABEL = "IMLC";

// Hidden providers are matched only on their "Provider #xxxx" label and
// non-identifying fields. Employers are skipped until reveal because a
// distinctive employer could identify them.
function getCandidateSearchTerms(candidate: Candidate): string[] {
  const terms = [
    getCandidateDisplayName(candidate),
    candidate.healthcareProfessionalType?.name,
    ...(candidate.specialties ?? []).map((specialty) => specialty.name),
    ...candidate.stateLicenses,
  ];
  if (candidate.hasImlcLicense) {
    terms.push(IMLC_LICENSE_LABEL);
  }
  if (!isCandidateHidden(candidate)) {
    terms.push(...getRecentEmployers(candidate));
  }
  return terms.filter((term): term is string => !!term);
}

export function searchCandidates({
  candidates,
  query,
}: {
  candidates: Candidate[];
  query: string;
}): Candidate[] {
  const needle = query.trim().toLowerCase();
  if (needle === "") {
    return candidates;
  }
  return candidates.filter((candidate) =>
    getCandidateSearchTerms(candidate).some((term) =>
      term.toLowerCase().includes(needle),
    ),
  );
}

function getInterestedAtMillis(candidate: Candidate): number {
  if (!candidate.interestedAt) {
    return 0;
  }
  return new Date(candidate.interestedAt).getTime();
}

export function sortCandidates({
  candidates,
  sort,
}: {
  candidates: Candidate[];
  sort: CandidateSort;
}): Candidate[] {
  const sorted = candidates.slice();
  switch (sort) {
    case CandidateSort.OldestApplied:
      return sorted.sort(
        (a, b) => getInterestedAtMillis(a) - getInterestedAtMillis(b),
      );
    case CandidateSort.NameAscending:
      return sorted.sort((a, b) =>
        getCandidateDisplayName(a).localeCompare(getCandidateDisplayName(b)),
      );
    case CandidateSort.NewestApplied:
      return sorted.sort(
        (a, b) => getInterestedAtMillis(b) - getInterestedAtMillis(a),
      );
  }
}

export function paginate<T>({ items, page }: { items: T[]; page: number }): {
  pageItems: T[];
  pageCount: number;
  currentPage: number;
} {
  const pageCount = Math.max(1, Math.ceil(items.length / CANDIDATES_PER_PAGE));
  const currentPage = Math.min(Math.max(page, 1), pageCount);
  const start = (currentPage - 1) * CANDIDATES_PER_PAGE;
  return {
    pageItems: items.slice(start, start + CANDIDATES_PER_PAGE),
    pageCount,
    currentPage,
  };
}

export type CandidatePermissions = {
  canManageJobPosts: boolean;
  canManageAffiliations: boolean;
  canManageProviderPools: boolean;
  canRevealProviders: boolean;
};

export type LapsedRequirement = {
  id: string;
  displayLabel: string;
  severity: string;
};

export type CandidateFlags = {
  isRejected: boolean;
  /**
   * True for any candidate who ever withdrew, even if later rejected. The
   * card hides its actions for them, which is why this differs from the
   * Withdrawn view's narrower definition.
   */
  isWithdrawn: boolean;
  isHired: boolean;
  isHidden: boolean;
  /** Neither rejected nor withdrawn: the org can still act on them. */
  isActive: boolean;
};

export function getCandidateFlags(candidate: Candidate): CandidateFlags {
  const isRejected = isCandidateRejected(candidate);
  const isWithdrawn = !!candidate.withdrawnAt;
  return {
    isRejected,
    isWithdrawn,
    isHired: isCandidateHired(candidate),
    isHidden: isCandidateHidden(candidate),
    isActive: !isRejected && !isWithdrawn,
  };
}

export function getRecentEmployers(candidate: Candidate): string[] {
  return candidate.workExperience
    .map((work) => (work.value as { organization: string }).organization)
    .filter(Boolean);
}

export function formatCandidateSubtitle(candidate: Candidate): string {
  const professionalType = candidate.healthcareProfessionalType?.name;
  const firstSpecialty = candidate.specialties?.[0]?.name;
  return [professionalType, firstSpecialty].filter(Boolean).join(" · ");
}

const VIEW_LABELS: Record<CandidateViewKind, string> = {
  [CandidateViewKind.All]: "All",
  [CandidateViewKind.Hired]: "Hired",
  [CandidateViewKind.Rejected]: "Rejected",
  [CandidateViewKind.Withdrawn]: "Withdrawn",
};

export function getViewLabel({
  view,
  stages,
}: {
  view: CandidateViewKey;
  stages: HiringStage[];
}): string {
  const stageId = getStageIdFromViewKey(view);
  if (stageId === null) {
    return VIEW_LABELS[view as CandidateViewKind];
  }
  return stages.find((stage) => stage.id === stageId)?.name ?? "";
}
